/**
 * Read every row of a PostgREST query, page by page.
 *
 * PostgREST caps every response at `max_rows` (1000 on our Supabase projects)
 * and answers HTTP 206 with no error when it truncates. A multi-row read with
 * no `.range()` therefore returns the first 1000 rows and silently drops the
 * rest. Measured 2026-10-29 on the admin cast sheet: 1092 live assignments
 * matched, 1000 came back, and 92 cast assignments vanished from the page
 * while still present in the database.
 *
 * `buildPage(from, to)` must return the query with `.range(from, to)` applied
 * and an `.order()` on a unique column (usually `id`). Without a total order,
 * PostgreSQL may return rows in a different order on each request, and pages
 * would overlap or skip rows.
 */
export const POSTGREST_MAX_ROWS = 1000;

/**
 * The least a page error must carry. `E` is inferred from `buildPage`, so a
 * Supabase builder yields its own `PostgrestError` (with `code`, `details`,
 * `hint`) and callers read those fields without a cast.
 */
export type PageError = { message: string };

type PageResponse<T, E extends PageError> = PromiseLike<{
  data: T[] | null;
  error: E | null;
}>;

export type FetchAllRowsResult<T, E extends PageError = PageError> =
  | { data: T[]; error: null }
  | { data: null; error: E };

export async function fetchAllRows<T, E extends PageError = PageError>(
  buildPage: (from: number, to: number) => PageResponse<T, E>,
  pageSize: number = POSTGREST_MAX_ROWS,
): Promise<FetchAllRowsResult<T, E>> {
  if (!Number.isInteger(pageSize) || pageSize < 1) {
    throw new RangeError(`fetchAllRows: invalid pageSize ${pageSize}`);
  }

  const rows: T[] = [];
  let from = 0;

  for (;;) {
    const { data, error } = await buildPage(from, from + pageSize - 1);

    if (error) {
      return { data: null, error };
    }

    const page = data ?? [];

    // Stop on an empty page, never on a short one: a server whose max_rows is
    // below pageSize returns short pages that are not the last, and stopping
    // there would reintroduce the silent truncation this helper exists for.
    if (page.length === 0) {
      return { data: rows, error: null };
    }

    rows.push(...page);
    from += page.length;
  }
}

export type FetchRowWindowResult<T, E extends PageError = PageError> =
  | { data: T[]; error: null; count: number | null }
  | { data: null; error: E; count: null };

/**
 * Read rows `from` to `to` (inclusive) of one ordered query, however many
 * requests the response cap needs. `.range(0, 9999)` alone returns the first
 * 1000 rows and no error.
 *
 * `buildPage(from, to)` must apply `.range(from, to)` and a total order, as for
 * `fetchAllRows`. `count` is the first response's `count`, so ask for
 * `{ count: 'exact' }` in the select when the caller needs a total.
 */
export async function fetchRowWindow<T, E extends PageError = PageError>(
  buildPage: (
    from: number,
    to: number,
  ) => PromiseLike<{
    data: T[] | null;
    error: E | null;
    count?: number | null;
  }>,
  from: number,
  to: number,
): Promise<FetchRowWindowResult<T, E>> {
  const rows: T[] = [];
  let count: number | null = null;
  let start = from;

  while (start <= to) {
    const response = await buildPage(start, to);

    if (response.error) {
      return { data: null, error: response.error, count: null };
    }

    if (start === from) {
      count = response.count ?? null;
    }

    const page = response.data ?? [];

    // Stop on an empty page, never on a short one (see fetchAllRows).
    if (page.length === 0) {
      break;
    }

    rows.push(...page);
    start += page.length;

    // Past the known total there is nothing left: skip the empty-page
    // request, which would recompute an exact count for nothing.
    if (count !== null && start >= count) {
      break;
    }
  }

  return { data: rows, error: null, count };
}
